import { z } from 'zod';
import { FEED_ROLE, FEED_TYPE } from './enums.js';

/**
 * The mapping config format.
 *
 * This file is the whole reason there is no supplier-specific code in the shop. A feed is
 * described entirely by data: which column is the sku, how numbers are written, how images
 * are collected, how a title is taken apart. Adding a supplier means adding one of these,
 * never editing catalog / pricing / cart / checkout / admin code.
 *
 * Every transform in the vocabulary below exists because a column in one of the five real
 * supplier files demands it. None were invented speculatively.
 */

/** `trim` — strip surrounding whitespace. Adams has `Kolor -  BRĄZOWY` with a double space. */
const TrimT = z.object({ op: z.literal('trim') });

/** `toNumber` — parse to a number, `null` on failure. Never defaults to 0. */
const ToNumberT = z.object({ op: z.literal('toNumber') });

/** `replaceComma` — `14,50` → `14.50`. All 4356 Adams numeric cells are comma-decimal. */
const ReplaceCommaT = z.object({
  op: z.literal('replaceComma'),
  decimalSeparator: z.string().default(','),
});

/** `stripHtml` — 47 Artisan `Short description` cells carry Teams copy-paste `<table>` residue. */
const StripHtmlT = z.object({
  op: z.literal('stripHtml'),
  allow: z.array(z.string()).default([]),
});

/** `stripQuotes` — 178 Artisan EANs are `'8904350022687'`, an Excel text-forcing artifact. */
const StripQuotesT = z.object({
  op: z.literal('stripQuotes'),
  chars: z.string().default("'\"`"),
});

/** `splitBy` — Artisan `Image URL` is a pipe-separated list of 8..45 urls. */
const SplitByT = z.object({
  op: z.literal('splitBy'),
  separator: z.string(),
  trimItems: z.boolean().default(true),
  dropEmpty: z.boolean().default(true),
});

/** `collectColumns` — Adams ships `image_link_1..15` as 15 discrete columns, not a list. */
const CollectColumnsT = z.object({
  op: z.literal('collectColumns'),
  columns: z.array(z.string()),
  dropEmpty: z.boolean().default(true),
});

/**
 * `encodeUrlPath` — 7671 of 10487 Adams image urls contain raw spaces, commas or
 * parentheses and 1216 contain non-ASCII. Percent-encode the path, leave the origin alone.
 */
const EncodeUrlPathT = z.object({ op: z.literal('encodeUrlPath') });

/**
 * `parseTitleVariants` — Adams encodes up to five variant axes inside the title string.
 * Separators and the axis vocabulary are parameters, so the next supplier that does this
 * needs no new code. `recoverTruncatedFrom` names a column used to repair values cut off
 * by the supplier's 150-character title limit.
 */
const ParseTitleVariantsT = z.object({
  op: z.literal('parseTitleVariants'),
  axisSeparators: z.array(z.string()).min(1),
  kvSeparators: z.array(z.string()).min(1),
  axisNames: z.array(z.string()),
  /** Fold two names for one concept, e.g. `Wymiar` and `Rozmiar łózka`. */
  axisAliases: z.record(z.string()).default({}),
  truncationLength: z.number().int().positive().nullable().default(null),
  recoverTruncatedFrom: z.record(z.string()).default({}),
  /**
   * Axis values that carry two facts in one string.
   *
   * Adams' `Kolor` axis mixes plain colours (`BEŻOWY`) with colour-plus-fabric-code
   * (`BEŻOWY - Malmo New 08`, `BEŻOWY - Paros 02`). Rendering that raw gives a 77-item
   * colour list where a third of the entries have no swatch, because a colour named after
   * a fabric matches nothing in the colour vocabulary.
   *
   * Declared here, the value splits: the head becomes the axis value and the tail lands on
   * the named attribute. **A value with no separator is kept whole**, never guessed at.
   */
  splitValueAxes: z
    .record(
      z.object({
        separator: z.string().default(' - '),
        /** Canonical attribute the tail is written to, e.g. `tkanina`. */
        tailAttribute: z.string(),
      }),
    )
    .default({}),
});

/** `parseTitleAttributes` — same grammar, but the pairs land as attributes, not variants. */
const ParseTitleAttributesT = ParseTitleVariantsT.omit({ op: true }).extend({
  op: z.literal('parseTitleAttributes'),
});

/**
 * `mapAlias` — `Narożnik` → `Narożniki`, `Palett` → `palett`, four spellings of Knock Down.
 *
 * `matchMode: 'contains'` exists because Artisan ships **no category column at all**; the
 * only categorical signal it sends is the product type inside the title (`Writing Desk`,
 * `Bedside`, `Coffee Table`). Reading that is using data the supplier does send, not
 * inventing a category — and an unmatched title still flags for review rather than
 * guessing. Longest key wins, so `Console Table` beats `Table`.
 */
const MapAliasT = z.object({
  op: z.literal('mapAlias'),
  aliases: z.record(z.string()),
  caseInsensitive: z.boolean().default(true),
  matchMode: z.enum(['exact', 'contains']).default('exact'),
  /** Unmapped values flag the product for review rather than inventing a value. */
  onUnmapped: z.enum(['passthrough', 'null', 'flag']).default('flag'),
  flagReason: z.string().optional(),
});

/**
 * `mapByRules` — derive a value from ordered patterns, first match wins.
 *
 * `mapAlias` in `contains` mode resolves ties by key length, which is a proxy for
 * specificity and not a good one: `side table` is longer than `bedside`, so bedside tables
 * were filed as side tables. Length says nothing about which rule the author meant to win.
 *
 * Here the order is written down. Specific patterns come first, general ones last, and the
 * first match wins — so `bedside` can be placed above `table` and stay there no matter how
 * either string is later edited.
 */
const MapByRulesT = z.object({
  op: z.literal('mapByRules'),
  rules: z.array(z.object({ when: z.string(), to: z.string() })),
  /** Unmapped values flag for review rather than inventing a category. */
  onUnmapped: z.enum(['passthrough', 'null', 'flag']).default('flag'),
  flagReason: z.string().optional(),
});

/**
 * `refineByColumn` — split one supplier value into several using another column.
 *
 * Suppliers combine categories that a storefront has to keep apart. Adams ships a single
 * `Łóżka i materace` — beds *and* mattresses — so an alias has to choose one, and whichever
 * it chooses files the other kind wrongly: four products literally named `Materac …` sat in
 * the beds category, and the beds category's photograph was a mattress cutaway.
 *
 * The rule is generic and the data lives in the config, so this stays on the right side of
 * "nothing supplier specific outside a mapping config". It refines a value that has already
 * been mapped, which keeps the alias table readable and makes the exception explicit.
 */
const RefineByColumnT = z.object({
  op: z.literal('refineByColumn'),
  /** The column consulted to decide, usually the product title. */
  column: z.string(),
  rules: z.array(
    z.object({
      /** Only refine when the value is already this; omit to apply regardless. */
      from: z.string().optional(),
      /** Case-insensitive regular expression tested against the column. */
      when: z.string(),
      to: z.string(),
    }),
  ),
});

/** `multiply` — unit conversion, e.g. cm³ → m³. */
const MultiplyT = z.object({ op: z.literal('multiply'), factor: z.number() });

/** `constant` — a field the feed does not carry, e.g. Adams currency. */
const ConstantT = z.object({ op: z.literal('constant'), value: z.unknown() });

/** `concat` — join several columns into one field. */
const ConcatT = z.object({
  op: z.literal('concat'),
  columns: z.array(z.string()),
  separator: z.string().default(' '),
});

/** `booleanFlag` — Artisan `Worldwide Shipping` is `1` or blank: a flag, not a number. */
const BooleanFlagT = z.object({
  op: z.literal('booleanFlag'),
  truthy: z.array(z.string()).default(['1', 'true', 'yes', 'tak']),
});

/** `splitColorFabric` — `BEŻOWY - Malmo New 08` is a colour AND a fabric code in one cell. */
const SplitColorFabricT = z.object({
  op: z.literal('splitColorFabric'),
  separator: z.string().default(' - '),
  part: z.enum(['color', 'fabric']),
});

/** `fabricFamily` — fold 183 fabric values (`HUGO 01`, `Kronos 34`) to ~26 case-folded families. */
const FabricFamilyT = z.object({ op: z.literal('fabricFamily') });

/** `nullIf` — `not applicable` and `-` both mean "no value"; they are not the same string. */
const NullIfT = z.object({
  op: z.literal('nullIf'),
  values: z.array(z.string()),
  caseInsensitive: z.boolean().default(true),
});

/** `parcelSpecs` — collapse `parcel_1_h/w/d/weight` … `parcel_9_*` into a ParcelSpec[]. */
const ParcelSpecsT = z.object({
  op: z.literal('parcelSpecs'),
  indexRange: z.tuple([z.number().int(), z.number().int()]),
  pattern: z.string(),
  fields: z.record(z.string()),
});

/** `validateBarcode` — check digit; a failure flags the barcode, it never blocks the row. */
const ValidateBarcodeT = z.object({
  op: z.literal('validateBarcode'),
  flagReason: z.string().default('invalid_barcode'),
});

/** `computeVolume` — h·w·d from real dimensions. Never trust a supplied volume column. */
const ComputeVolumeT = z.object({
  op: z.literal('computeVolume'),
  columns: z.tuple([z.string(), z.string(), z.string()]),
  /** cm → m³ */
  divisor: z.number().default(1_000_000),
  /** Untrusted column to compare against; disagreement beyond tolerance flags the row. */
  crossCheckColumn: z.string().optional(),
  crossCheckTolerance: z.number().default(0.05),
  flagReason: z.string().default('volume_mismatch'),
});

export const TransformSchema = z.discriminatedUnion('op', [
  TrimT,
  ToNumberT,
  ReplaceCommaT,
  StripHtmlT,
  StripQuotesT,
  SplitByT,
  CollectColumnsT,
  EncodeUrlPathT,
  ParseTitleVariantsT,
  ParseTitleAttributesT,
  MapAliasT,
  MultiplyT,
  ConstantT,
  ConcatT,
  BooleanFlagT,
  SplitColorFabricT,
  FabricFamilyT,
  NullIfT,
  ParcelSpecsT,
  ValidateBarcodeT,
  ComputeVolumeT,
  RefineByColumnT,
  MapByRulesT,
]);
export type Transform = z.infer<typeof TransformSchema>;
export type TransformOp = Transform['op'];

/** One canonical field, and how to get there from the raw row. */
export const FieldMappingSchema = z.object({
  /** Canonical target, e.g. `supplierSku`, `purchasePrice`, `imageUrls`. */
  target: z.string(),
  /** Source column. Omitted when the value comes purely from transforms (`constant`). */
  column: z.string().optional(),
  transforms: z.array(TransformSchema).default([]),
  required: z.boolean().default(false),
  /**
   * What happens when the value is missing. `flag` is the default and the whole point:
   * a missing field flags the product for review, it never gets a default value.
   */
  onMissing: z.enum(['flag', 'null', 'skipRow']).default('flag'),
  flagReason: z.string().optional(),
});
export type FieldMapping = z.infer<typeof FieldMappingSchema>;

/** How several feeds of one supplier are joined. Declared, never coded. */
export const JoinConfigSchema = z.object({
  /** Feed key of the joined feed. */
  feed: z.string(),
  /** Column in the joined feed holding the join key. */
  joinKey: z.string(),
  /** Canonical field in the base record to match against. */
  baseKey: z.string().default('supplierSku'),
  /**
   * Field precedence. `joined` means this feed beats the catalog — that is how
   * `New Prices` overrides the catalog price: a priority declaration, not an `if`.
   */
  precedence: z.enum(['base', 'joined']).default('joined'),
  fields: z.array(FieldMappingSchema),
  /** Rows in the joined feed with no matching base row: staged and counted, never created. */
  onUnmatched: z.enum(['stage', 'ignore', 'error']).default('stage'),
  /** Base rows with no joined row. `flag` for stock — resolves to zero AND flags. */
  onMissing: z.enum(['flag', 'null']).default('flag'),
  missingFlagReason: z.string().optional(),
});
export type JoinConfig = z.infer<typeof JoinConfigSchema>;

export const GuardConfigSchema = z.object({
  abortIfEmpty: z.boolean().default(true),
  maxRowCountDropPercent: z.number().default(30),
  maxCoverageDropPoints: z.number().default(10),
  maxPriceChangeFactor: z.number().default(5),
  flagPriceDecreasePercent: z.number().default(20),
  /** Stricter than the spec: flag every decrease, not only those over the threshold. */
  flagAllPriceDecreases: z.boolean().default(true),
  maxRows: z.number().default(500_000),
  maxCellBytes: z.number().default(100_000),
});
export type GuardConfig = z.infer<typeof GuardConfigSchema>;

export const FeedConfigSchema = z.object({
  key: z.string(),
  supplierCode: z.string(),
  type: z.enum(FEED_TYPE),
  role: z.enum(FEED_ROLE),
  source: z.string(),
  encoding: z.string().default('utf-8'),
  delimiter: z.string().default(','),
  decimalSeparator: z.string().default('.'),
  /** Quoted newlines are mandatory: the Adams file has CR inside quoted descriptions. */
  quoteChar: z.string().default('"'),
  hasHeader: z.boolean().default(true),
  currency: z.string().nullable().default(null),
  schedule: z.string().nullable().default(null),
  /** Resolves field conflicts between feeds of the same supplier. Higher wins. */
  priority: z.number().int().default(0),
  identity: z.object({
    skuColumn: z.string(),
    eanColumn: z.string().optional(),
  }),
  fields: z.array(FieldMappingSchema).default([]),
  joins: z.array(JoinConfigSchema).default([]),
  categoryAliases: z.record(z.string()).default({}),
  guards: GuardConfigSchema.default({}),
});
export type FeedConfig = z.infer<typeof FeedConfigSchema>;
